import React, {
	AriaRole,
	ComponentType,
	FC,
	KeyboardEvent,
	KeyboardEventHandler,
	MouseEvent,
	MouseEventHandler,
	useCallback,
} from 'react';

import { useKeyboardActivation } from '../hooks/useKeyboardActivation';

function defaultButtonRoleForStyledTarget(wrapped: { target?: unknown }): string | undefined {
	const target = wrapped.target;
	// styled(Grid) etc.: .target is a component; treat like a generic interactive surface.
	if (typeof target !== 'string') {
		return 'button';
	}
	return target === 'div' || target === 'span' ? 'button' : undefined;
}

export interface WithAccessibilityProps {
	onClick?: MouseEventHandler<HTMLElement>;
	onKeyDown?: KeyboardEventHandler<HTMLElement>;
	role?: AriaRole;
	tabIndex?: number;
}

/**
 * Wraps components that use `onClick` with keyboard activation (Enter/Space), focus order (`tabIndex`),
 * and a default `role="button"` where appropriate.
 *
 * For other intrinsic tags (e.g. `li`, `a`, table cells), the default role is omitted so native implicit
 * roles stay correct. Composite targets (`styled(SomeComponent)`) still default to `button`. Pass `role`
 * when you need something else (e.g. `option` in a listbox).
 *
 * @example
 * const InteractiveDiv = withAccessibility(({ onClick, ...props }) => (
 *   <div onClick={onClick} {...props} />
 * ));
 * <InteractiveDiv onClick={handleClick} />
 */
export const withAccessibility = <P extends WithAccessibilityProps>(WrappedComponent: ComponentType<P>): FC<P> => {
	const AccessibleComponent: FC<P> = ({ onClick, onKeyDown, role, tabIndex, ...restProps }: P) => {
		const handleActivation = useKeyboardActivation((e: KeyboardEvent<HTMLElement>) => {
			onClick?.(e as unknown as MouseEvent<HTMLElement>);
		});
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				handleActivation(e);
				onKeyDown?.(e);
			},
			[handleActivation, onKeyDown]
		);

		if (!onClick) {
			return <WrappedComponent {...({ ...restProps, onClick, onKeyDown, role, tabIndex } as P)} />;
		}

		const defaultRole = defaultButtonRoleForStyledTarget(WrappedComponent as { target?: unknown });

		return (
			<WrappedComponent
				{...(restProps as P)}
				onClick={onClick}
				onKeyDown={handleKeyDown}
				role={role ?? defaultRole}
				tabIndex={tabIndex ?? 0}
			/>
		);
	};

	AccessibleComponent.displayName = `withAccessibility(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

	return AccessibleComponent;
};

export default withAccessibility;
